import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue} from "recoil";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {Button, Chip, Grid, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import IconButton from "@mui/material/IconButton";
import {DescriptionRounded, WarningRounded} from "@mui/icons-material";
import {FilterDef} from "../../../constants/FinanceModels";
import EmptyState from "../../../../../util/component/EmptyState";
import {getLocalGridState} from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {useFuelCardsRepository} from "../../../api/repositories/FuelCardsRepository";
import {STATE_FUEL_ACTIVE_FILTERS, STATE_FUEL_TABLE_UPDATING} from "../constants/FuelCardRecoil";
import FuelModal from "../modals/FuelModal";
import FuelCardFilter from "../filters/FuelCardFilter";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FinanceDialog from "../../../component/FinanceDialog";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import FuelCardDetail from "../pages/FuelCardDetail";
import {useFuelStatuses} from "../constants/useFuelStatuses";
import {formatCurrency, formatDate} from "../../../utils/FormatUtils";
import {SCOPE_FUEL_CARD_DELETE, SCOPE_FUEL_CARD_EDIT, SCOPE_FUEL_CARD_VIEW_DETAIL} from "../constants/FuelCardAuth";
import {useHasScope} from "../../../../../config/globalConfig";
import {useFuelCategories} from "../constants/useFuelCategories";
import dayjs from "dayjs";
import {IsRowSelectable, RowNode, SelectionChangedEvent} from "ag-grid-community";
import {TO_BE_ANALYZED} from "../constants/FuelsConstants";

const FuelCardTable = (props: any) => {

    const UNIQUE_ID = "fnn-fuel-cards";
    let BACKTOFUELCARDSLIST = "/finance/fuelcards";
    const {id} = useParams();
    const {t} = useTranslation("finance", {keyPrefix: "fuel"});
    const {getReportById, getFuelCard, deleteRefuel, deleteRefuelsList} = useFuelCardsRepository();
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [updating, setUpdating] = useRecoilState(STATE_FUEL_TABLE_UPDATING);
    const filterDefs = useRecoilValue(STATE_FUEL_ACTIVE_FILTERS);
    const [openFuelModal, setOpenFuelModal] = useState(false);
    const [fuelCard, setFuelCard] = useState<any>(undefined);
    const [fuelCardReport, setFuelCardReport] = useState<any>(undefined);
    const gridRef = useRef<AgGridReact>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openDeleteMoreDialog, setOpenDeleteMoreDialog] = useState(false);
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [isReadonly, setIsReadonlyDetail] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const {getStatus, getStatusColor} = useFuelStatuses();
    const {getCategories} = useFuelCategories();
    const hasScope = useHasScope;
    const [selectedItemsInternal, setSelectedItemsInternal] = useState<any | undefined>();
    const [items, setItems] = useState<any | undefined>();

    useEffect(() => {
        if (id !== undefined) {
            getReportById(id).then(data => {
                setFuelCardReport(data.result);
            })
        }
    }, [id]);


    const columnDefs: ColDef[] = [
        {
            field: "supplier",
            headerName: t("tables.supplier"),
            unSortIcon: true, sortable: true,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            lockVisible: true, lockPinned: true,
            showDisabledCheckboxes: true,
            cellRenderer: (params: any) => params.value,
            tooltipValueGetter: (params: any) => params.value,
        },
        {
            field: "raNumber", headerName: t("tables.ra"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value,
            tooltipValueGetter: (params: any) => params.value,
        },
        {
            field: "vehicleSapNumber", headerName: t("tables.carNumber"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value,
            tooltipValueGetter: (params: any) => params.value,
        },
        {
            field: "cardCustomer", headerName: t("tables.cardCustomer"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value,
        },
        {
            field: "cardNumber", headerName: t("tables.cardNumber"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value,
            tooltipValueGetter: (params: any) => params.value,
        },
        {
            field: "transactionDatetime", headerName: t("details.refuellingDate"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value ? formatDate(params.value) : "",
            tooltipValueGetter: (params: any) => params.value ? formatDate(params.value) : "",
        },
        {
            field: "amount", headerName: t("details.amount"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value ? formatCurrency(params.value, params.data.currency, 0.01) : "",
            tooltipValueGetter: (params: any) => params.value ? formatCurrency(params.value, params.data.currency, 0.01) : ""
        },
        {
            field: "invoiceNumber", headerName: t("details.invoiceNumber"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value,
            tooltipValueGetter: (params: any) => params.value,
        },
        {
            field: "invoiceDate", headerName: t("details.invoiceDate"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            tooltipValueGetter: (params: any) => params.value ? dayjs(params.value).format("L") : "",
        },
        {
            field: "category", headerName: t("tables.category"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => getCategories(params.value),
            tooltipValueGetter: (params: any) => getCategories(params.value),
        },
        {
            field: "status", headerName: t("tables.status"),
            unSortIcon: true, sortable: true,
            lockPosition: "right", lockVisible: true, lockPinned: true, pinned: "right",
            cellRenderer: (params: any) => {
                return <Tooltip title={params.data.errorMessage ? t('messages.' + params.data.errorMessage) : ""}>
                    <Chip label={getStatus(params.value)}
                          color={getStatusColor(params.value)}/>
                </Tooltip>
            }
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => (
                        <FinanceDynamicActions>
                            {hasScope(SCOPE_FUEL_CARD_VIEW_DETAIL) &&
                                <Tooltip arrow title={t('tables.tooltips.showDetail')} placement={"left"}>
                                    <IconButton
                                        sx={{color: theme.palette.accent.main}}
                                        onClick={() => {
                                            setFuelCard(params.data);
                                            setOpenDetailModal(true);
                                            setIsReadonlyDetail(true);
                                        }}
                                    >
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>}
                            {hasScope(SCOPE_FUEL_CARD_EDIT) &&
                                <Tooltip arrow title={t('tables.tooltips.edit')} placement={"left"}>
                                    <IconButton
                                        sx={{color: theme.palette.accent.main}}
                                        disabled={params.data.status !== TO_BE_ANALYZED}
                                        onClick={() => {
                                            setFuelCard(params.data)
                                            setOpenDetailModal(true);
                                            setIsReadonlyDetail(false);
                                        }}
                                    >
                                        <EditIcon/>
                                    </IconButton>
                                </Tooltip>}
                            {hasScope(SCOPE_FUEL_CARD_DELETE) &&
                                <Tooltip arrow title={t('tables.tooltips.delete')} placement={"left"}>
                                    <IconButton
                                        sx={{color: theme.palette.accent.main}}
                                        onClick={() => {
                                            setFuelCard(params.data);
                                            setOpenDeleteDialog(true);
                                        }}
                                    >
                                        <DeleteIcon/>
                                    </IconButton>
                                </Tooltip>}
                        </FinanceDynamicActions>
                    ),
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        suppressRowClickSelection: true,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: true,
        },
        noRowsOverlayComponentParams: {
            title: t('tables.noRowsOverlay.title'),
            content: t("tables.noRowsOverlay.content")
        }
    };

    useEffect(() => {
        if(fuelCardReport){
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        }
    }, [fuelCardReport,filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating]);

    useEffect(() => {
        if (!openDetailModal) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(true);
        }
    }, [openDetailModal]);


    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (params: RowNode<any>) => {
            return !!params.data && params.data.id !== "";
        };
    }, []);

    const onSelectionChanged = (event: SelectionChangedEvent) => {
        let selectedRows = gridRef.current!.api.getSelectedRows();
        setSelectedItemsInternal(selectedRows.map(s => s.id));
    };

    useEffect(() => {
        setSelectedItemsInternal(props.selectedItems)
    }, [props.selectedItems]);


    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                getFuelCard(startRow, endRow - startRow, sort, order, filterDefs, id, fuelCardReport.legalEntity, fuelCardReport.dealer)
                    .then((response) => {
                        let result = response.result;
                        if (result.total === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.total,
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    const toolbar = {
        renderActions: () => (
            <Stack>
                {hasScope(SCOPE_FUEL_CARD_DELETE) &&
                    <Button size="small" variant={"contained"}
                            disabled={!(!!selectedItemsInternal) || (selectedItemsInternal && selectedItemsInternal.length == 0)}
                            onClick={() => {
                                setOpenDeleteMoreDialog(true);
                            }} startIcon={<DeleteIcon/>}
                    >  {t('details.buttons.deleteRefuels')}
                    </Button>}
            </Stack>
        )
    }
    const deleteRefuelRequest = () => {
        deleteRefuel(fuelCard.id).then(res => {
                if (res.status === "OK") {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("messages.deleteSuccess"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    setUpdating(true);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`snackBarErrors.${res.message}`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            }
        );
    }
    const deleteMoreRefuelRequest = () => {
        deleteRefuelsList(selectedItemsInternal).then(res => {
                if (res.status === "OK") {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("messages.deleteSuccess"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    setUpdating(true);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`snackBarErrors.${res.message}`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            }
        );
    }

    return (
        <>
            <Grid>
                <Typography variant={'body1'} fontWeight={'bold'}>
                    {(fuelCardReport?.supplier ? fuelCardReport.supplier : "") + " - "
                        + (fuelCardReport?.fileName ? fuelCardReport.fileName : "")}
                </Typography>
            </Grid>
            <FuelCardFilter/>
            <AgGridCustomTable
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                noRowsOverlayComponent={noRowsOverlayComponent}
                rowSelection={'multiple'}
                rowData={items}
                onSelectionChanged={onSelectionChanged}
                suppressRowClickSelection
                isRowSelectable={isRowSelectable}
                toolbar={toolbar}
            >
            </AgGridCustomTable>
            <FuelCardDetail open={openDetailModal} setOpen={setOpenDetailModal}
                            fuelCardProp={fuelCard} isReadonly={isReadonly}/>

            <FuelModal open={openFuelModal}
                       onClose={() => {
                           setOpenFuelModal(false)
                       }}
            />

            <FinanceDialog
                title={t(`modalDelete.title`)}
                content={t(`modalDelete.content`, {
                    cardNumber: fuelCard?.cardNumber,
                    transactionDatetime: formatDate(fuelCard?.transactionDatetime)
                })}
                cancelButton={t('modalDelete.buttons.cancel')}
                open={openDeleteDialog}
                onClose={() => {
                    setOpenDeleteDialog(false);
                }}
                confirmButton={t('modalDelete.buttons.deleteRefuel')}
                callback={() => {
                    deleteRefuelRequest();
                    setOpenDeleteDialog(false);
                }}
                titleIcon={<WarningRounded/>}
            />
            <FinanceDialog
                title={t(`modalDelete.title`)}
                content={t(`modalDelete.contentMore`, {refuel: selectedItemsInternal ? selectedItemsInternal.length : 0})}
                cancelButton={t('modalDelete.buttons.cancel')}
                open={openDeleteMoreDialog}
                onClose={() => {
                    setOpenDeleteMoreDialog(false);
                }}
                confirmButton={t('modalDelete.buttons.deleteRefuel')}
                callback={() => {
                    deleteMoreRefuelRequest();
                    setOpenDeleteMoreDialog(false);
                }}
                titleIcon={<WarningRounded/>}
            />
        </>

    );
}

export default FuelCardTable;